import React, { useContext } from 'react';

import { fileUploadApis } from '@apis/next/fileUploadApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import { StatusFlowsContext } from '@components/admin/booking/BookingStatusFlows';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { FileInput } from '@components/NextForms/FileInput';
import { TextInput } from '@components/NextForms/TextInput';
import { useFormUtils } from '@hooks/useFormUtils';
import { FileTypeHelper, RemoteFile } from '@models/next/FileType';
import { DOInfoForm } from '@models/next/StatusFlows';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  jp: {
    doProcessCompleted: 'D/O手配済',
    update: '変更',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.textGray700,
    ...common.middleText,
    padding: '1.5rem 1.5rem',
  },
  field: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '1fr 2fr',
  },
  split: {
    borderWidth: 0.5,
    borderColor: theme.palette.grey['300'],
    borderStyle: 'dashed',
    flex: 1,
    margin: '1rem 0',
  },
  button: {
    width: '132px',
  },
}));

export interface DOUpdateDialogOptions {
  open: boolean;
  edit: boolean;
  DOFile?: RemoteFile | null;
  DONo?: string | null;
  onClose: () => void;
}

export const DOUpdateDialog: React.FC<DOUpdateDialogOptions> = (props) => {
  const { open, edit, onClose, DOFile, DONo } = props;
  const classes = useStyles();
  const { d2dNo, invalidateStatusFlows } = useContext(StatusFlowsContext);

  const postFileUpload = fileUploadApis.usePost('/file/upload');
  const patchPreAlert = dashboardPreAlertApis.usePatch(
    '/dashboard/pre-alert/:d2d_no',
    {
      params: { d2d_no: d2dNo },
    },
    { onSuccess: () => invalidateStatusFlows() },
  );

  const form = useForm(DOInfoForm, {
    initialOutput: {
      do_less_no: DONo,
      do_less_no_file: DOFile ? FileTypeHelper.remoteToLocal(DOFile) : undefined,
    },
  });
  const { fields } = form.root;
  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postFileUpload, patchPreAlert],
  });

  return (
    <ActionableDialog open={open} onClose={onClose}>
      <ActionableDialogTitle>
        {strings.doProcessCompleted}
        {edit && `-${strings.update}`}
      </ActionableDialogTitle>
      {renderAlertsAndSpinnerModal()}
      <form
        {...form.bindForm({
          async onSubmit(data) {
            const [doFile] = data.do_less_no_file?.file
              ? data.do_less_no_file?.remote
                ? [FileTypeHelper.localToRemote(data.do_less_no_file)]
                : await postFileUpload.mutateAsync({
                    file: [data.do_less_no_file.file],
                  })
              : [];
            await patchPreAlert.mutateAsync({
              do_less_no_file: doFile,
              do_less_no: data.do_less_no ?? undefined,
              do_status_name: edit ? undefined : 'D/O手配済',
            });

            onClose();
          },
          onSubmitError(errors) {
            console.error(JSON.stringify(form.rawInput));
            console.error(errors);
          },
        })}
      >
        <ActionableDialogContent className={classes.dialogContent}>
          <div className={classes.field}>
            <label>{fields.do_less_no_file.formMeta.label}</label>
            <div>
              <FileInput
                field={fields.do_less_no_file}
                hideButtonIfHasFile
                accept={'application/pdf'}
                buttonProps={{
                  shape: 'round',
                  className: classes.button,
                }}
              />
            </div>
          </div>
          <div className={classes.split}></div>
          <div className={classes.field}>
            <label>{fields.do_less_no.formMeta.label}</label>
            <div>
              <TextInput field={fields.do_less_no} />
            </div>
          </div>
        </ActionableDialogContent>
        <ActionableDialogActions>
          <Button
            color="grey"
            text={formStrings.cancel}
            size="small"
            type="button"
            onClick={() => onClose()}
          />
          <Button
            color="primary"
            text={edit ? formStrings.ok : strings.doProcessCompleted}
            size="small"
            type="submit"
          />
        </ActionableDialogActions>
      </form>
    </ActionableDialog>
  );
};
